import { motion } from 'framer-motion';
import MathBlock from './MathBlock';

interface Props {
  index: number;
  title: string;
  explanation: string;
  math?: string;
}

export default function StepCard({ index, title, explanation, math }: Props) {
  const sentences = splitIntoSentences(explanation);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08 }}
      className="card p-5"
    >
      <div className="flex items-start gap-4">
        <div className="shrink-0 w-9 h-9 rounded-full bg-coral/10 text-coral font-display font-bold flex items-center justify-center">
          {index + 1}
        </div>
        <div className="flex-1 space-y-3">
          <h3 className="font-display text-lg text-ink leading-snug">{title}</h3>

          {sentences.length > 1 ? (
            <ul className="space-y-2">
              {sentences.map((s, i) => (
                <li key={i} className="flex gap-2.5 text-ink/80 leading-relaxed">
                  <span className="text-coral font-bold shrink-0 mt-0.5">•</span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-ink/80 leading-relaxed">{explanation}</p>
          )}

          {math && <MathBlock math={math} stepTitle={title} />}
        </div>
      </div>
    </motion.div>
  );
}

// Split a paragraph into sentences. Guards against decimals ("3.14") and
// common abbreviations ("e.g.", "i.e.") by requiring the next character after
// the punctuation to start a real word (capital letter, quote, paren, or "A").
function splitIntoSentences(text: string): string[] {
  if (!text) return [];
  // Replace common abbreviations with placeholders so they don't break on the period.
  const guarded = text
    .replace(/\be\.g\./gi, 'e·g·')
    .replace(/\bi\.e\./gi, 'i·e·')
    .replace(/\betc\./gi, 'etc·')
    .replace(/\bvs\./gi, 'vs·')
    .replace(/\bDr\./gi, 'Dr·')
    .replace(/\bMr\./gi, 'Mr·')
    .replace(/\bMrs\./gi, 'Mrs·')
    // Number-point-number like 3.14 — protect the inner dot.
    .replace(/(\d)\.(\d)/g, '$1·$2');

  const parts = guarded
    .split(/(?<=[.!?])\s+(?=[A-Z"'(\d])/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => s.replace(/·/g, '.'));

  return parts;
}
